import React, { useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLanguage,
  FaUserTag,
  FaClock,
  FaInfoCircle,
  FaCheckCircle,
  FaMoneyBillWave,
  FaShieldAlt,
  FaBirthdayCake,
} from "react-icons/fa";
import config, { getUserCity } from "../../../config";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

const LifeInsuranceAgent = () => {
  const navigate = useNavigate();

  const [activeForm, setActiveForm] = useState("post");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const categoryKey = "insurance-services";
  const serviceKey = "life-insurance";

  const areaOptions = config.AREA_OPTIONS || {};
  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService =
    config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];

  const serviceLabel = selectedService?.label || "Life Insurance";
  const categoryLabel = selectedCategory?.label || "Insurance Services";

  const INSURANCE_OPTIONS = selectedService?.types || [];

  const [formData, setFormData] = useState({
    username: "",
    mobilenumber: "",
    email: "",
    address: "",
    city: "",
    area: "",
    language: "",
    gender: "",
    age: "",
    urgencyStatus: "",
    annualIncome: "",
    sumAssured: "",
    smokerStatus: "",
    insurancePlan: "",
    additionalNotes: "",
  });

  const [searchCriteria, setSearchCriteria] = useState({
    gender: "",
    city: "",
    area: "",
  });

  const availableAreas = useMemo(() => {
    const selected =
      areaOptions[formData.city] || areaOptions[formData.city?.toLowerCase()];
    if (Array.isArray(selected)) return selected;
    if (selected?.areas) return selected.areas;
    return [];
  }, [formData.city, areaOptions]);

  const searchAreas = useMemo(() => {
    const selected =
      areaOptions[searchCriteria.city] ||
      areaOptions[searchCriteria.city?.toLowerCase()];
    if (Array.isArray(selected)) return selected;
    if (selected?.areas) return selected.areas;
    return [];
  }, [searchCriteria.city, areaOptions]);

  const styles = {
    page: {
      minHeight: "100vh",
      background:
        "linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #eef2ff 100%)",
      padding: "30px 20px",
      fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
    },
    wrapper: {
      maxWidth: "1580px",
      width: "96%",
      margin: "0 auto",
    },
    hero: {
      background: "linear-gradient(135deg, #0f172a, #1d4ed8, #38bdf8)",
      color: "#fff",
      borderRadius: "28px",
      padding: "34px 36px",
      boxShadow: "0 20px 40px rgba(37, 99, 235, 0.22)",
      marginBottom: "24px",
    },
    heroBadge: {
      display: "inline-block",
      background: "rgba(255,255,255,0.14)",
      padding: "7px 14px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: "700",
      marginBottom: "12px",
    },
    heroTitle: {
      margin: 0,
      fontSize: "2.1rem",
      fontWeight: "800",
    },
    heroText: {
      marginTop: "10px",
      marginBottom: 0,
      color: "rgba(255,255,255,0.9)",
      maxWidth: "900px",
      lineHeight: 1.6,
      fontSize: "15px",
    },
    switchWrap: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      marginBottom: "22px",
    },
    switchBtn: {
      border: "1px solid #cbd5e1",
      borderRadius: "999px",
      padding: "12px 20px",
      background: "#fff",
      color: "#334155",
      fontSize: "14px",
      fontWeight: "800",
      cursor: "pointer",
      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
    },
    switchBtnActive: {
      background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
      color: "#fff",
      border: "1px solid #2563eb",
      boxShadow: "0 12px 24px rgba(37, 99, 235, 0.22)",
    },
    formCard: {
      background: "#fff",
      borderRadius: "28px",
      border: "1px solid #e5edf8",
      boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
      overflow: "hidden",
      width: "100%",
      maxWidth: "1320px",
      margin: "0 auto",
    },
    formHeader: {
      padding: "28px 40px",
      borderBottom: "1px solid #edf2f7",
      background: "linear-gradient(180deg, #ffffff, #f8fbff)",
    },
    formTitle: {
      margin: 0,
      fontSize: "1.6rem",
      fontWeight: "800",
      color: "#0f172a",
    },
    formSub: {
      margin: "8px 0 0 0",
      color: "#64748b",
      fontSize: "14px",
    },
    body: {
      padding: "32px 40px 36px",
    },
    section: {
      marginBottom: "28px",
      padding: "24px",
      borderRadius: "20px",
      background: "#f8fbff",
      border: "1px solid #e6eef8",
    },
    sectionTitle: {
      margin: "0 0 18px 0",
      fontSize: "1.1rem",
      fontWeight: "800",
      color: "#0f172a",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "20px",
      alignItems: "start",
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#334155",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      minHeight: "120px",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
      resize: "vertical",
    },
    submitWrap: {
      display: "flex",
      justifyContent: "center",
      marginTop: "30px",
    },
    submitBtn: {
      border: "none",
      borderRadius: "999px",
      background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
      color: "#fff",
      padding: "15px 34px",
      fontSize: "15px",
      fontWeight: "800",
      minWidth: "260px",
      boxShadow: "0 12px 24px rgba(37, 99, 235, 0.24)",
      cursor: "pointer",
    },
    helper: {
      color: "#64748b",
      fontSize: "13px",
      margin: 0,
      lineHeight: 1.6,
    },
    summaryBox: {
      marginTop: "12px",
      fontSize: "14px",
      color: "#0f172a",
    },
    errorText: {
      color: "#dc2626",
      fontSize: "14px",
      fontWeight: "600",
      marginTop: "12px",
    },
    popupOverlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    },
    popupCard: {
      background: "#fff",
      borderRadius: "18px",
      padding: "22px 24px",
      maxWidth: "360px",
      width: "90%",
      textAlign: "center",
      boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
    },
popupTitle: {
  margin: "10px 0 6px 0",
  fontSize: "1.1rem",
  fontWeight: "800",
  color: "#1d4ed8",
},
    popupText: {
      margin: 0,
      fontSize: "14px",
      color: "#4b5563",
    },
popupButton: {
  marginTop: "16px",
  borderRadius: "999px",
  border: "none",
  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "700",
  padding: "10px 24px",
  cursor: "pointer",
  boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
},
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let cleanValue = value;

    if (name === "mobilenumber") {
      cleanValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "age") {
      cleanValue = value.replace(/\D/g, "").slice(0, 2);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: cleanValue,
      ...(name === "city" ? { area: "" } : {}),
    }));
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "city" ? { area: "" } : {}),
    }));
  };

  const handleSearchCheckLabour = async (e) => {
    e.preventDefault();

    if (!searchCriteria.gender || !searchCriteria.city || !searchCriteria.area) {
      alert("Please fill all fields before searching");
      return;
    }

    try {
      setIsSearching(true);

      const requestBody = {
        ...searchCriteria,
        work_category: "life-insurance",
        service_category: categoryKey,
        service: serviceKey,
      };

      const response = await fetch(
        `${BASE_URL}/api/search/labour-life-insurance/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (response.ok) {
        navigate("/labour-list", {
          state: {
            labours: data.labours,
            work_category: "Life Insurance",
          },
        });
      } else {
        alert(data.error || "Failed to search labour.");
      }
    } catch (error) {
      console.error("Request failed:", error);
      alert("Request failed while searching labour.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!formData.insurancePlan) {
      setSubmitError("Please select an insurance plan.");
      return;
    }

    const selectedPlan = INSURANCE_OPTIONS.find(
      (item) => item.value === formData.insurancePlan
    );

    const serviceList = [formData.insurancePlan];

    const detailedServiceList = selectedPlan
      ? [
          {
            type: "life-insurance",
            key: selectedPlan.value,
            value: selectedPlan.value,
            label: selectedPlan.label,
          },
        ]
      : [];

    const requestBody = {
      username: formData.username,
      mobilenumber: formData.mobilenumber,
      email: formData.email,
      address: formData.address,
      city: formData.city,
      area: formData.area,
      language: formData.language,
      gender: formData.gender,
      age: formData.age,
      urgencyStatus: formData.urgencyStatus,
      annualIncome: formData.annualIncome,
      sumAssured: formData.sumAssured,
      smokerStatus: formData.smokerStatus,
      additionalNotes: formData.additionalNotes,
      service_category: categoryKey,
      selected_city: getUserCity(),
      service_type: "life-insurance-services",
      service_list: serviceList,
      detailed_service_list: detailedServiceList,
      service: serviceKey,
      category_label: categoryLabel,
      service_label: serviceLabel,
    };

    console.log("Sending payload:", requestBody);

    try {
      setIsSubmitting(true);

      const response = await axios.post(
        `${BASE_URL}/api/service/requirements/`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setShowThankYou(true);

        setFormData({
          username: "",
          mobilenumber: "",
          email: "",
          address: "",
          city: "",
          area: "",
          language: "",
          gender: "",
          age: "",
          urgencyStatus: "",
          annualIncome: "",
          sumAssured: "",
          smokerStatus: "",
          insurancePlan: "",
          additionalNotes: "",
        });
      }
    } catch (error) {
      console.log("API Error:", error.response?.data);

      const err = error.response?.data;

      if (err?.error) {
        setSubmitError(err.error);
      } else if (typeof err === "string") {
        setSubmitError(err);
      } else if (err && typeof err === "object") {
        const firstKey = Object.keys(err)[0];
        const firstValue = err[firstKey];
        if (Array.isArray(firstValue)) {
          setSubmitError(firstValue[0]);
        } else if (typeof firstValue === "string") {
          setSubmitError(firstValue);
        } else {
          setSubmitError("Failed to submit requirement");
        }
      } else {
        setSubmitError("Failed to submit requirement");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedInsuranceLabel =
    INSURANCE_OPTIONS.find((item) => item.value === formData.insurancePlan)
      ?.label || "";

  return (
    <>
      {showThankYou && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <FaCheckCircle color="#16a34a" size={32} />
            <h3 style={styles.popupTitle}>Thank you!</h3>
            <p style={styles.popupText}>
              Your requirement has been submitted. We will get back to you
              shortly.
            </p>
            <button
              type="button"
              style={styles.popupButton}
              onClick={() => {
                setShowThankYou(false);
                navigate("/");
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div style={styles.page}>
        <div style={styles.wrapper}>
          <div style={styles.hero}>
            <div style={styles.heroBadge}>{serviceLabel}</div>
            <h1 style={styles.heroTitle}>
              Buy Life Insurance or Search Insurance Agent
            </h1>
            <p style={styles.heroText}>
              Submit your life insurance requirement with plan details, personal
              information, income range, and sum assured, or search available
              insurance agents by city and area.
            </p>
          </div>

          <div style={styles.switchWrap}>
            <button
              type="button"
              style={{
                ...styles.switchBtn,
                ...(activeForm === "post" ? styles.switchBtnActive : {}),
              }}
              onClick={() => setActiveForm("post")}
            >
              Post Your Requirement
            </button>

            <button
              type="button"
              style={{
                ...styles.switchBtn,
                ...(activeForm === "search" ? styles.switchBtnActive : {}),
              }}
              onClick={() => setActiveForm("search")}
            >
              Check Agent
            </button>
          </div>

          {activeForm === "post" ? (
            <form style={styles.formCard} onSubmit={handleSubmit}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Life Insurance Requirement Form</h2>
                <p style={styles.formSub}>
                  Fill in your details, choose an insurance plan, and share your
                  coverage requirement.
                </p>
              </div>

              <div style={styles.body}>
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Personal Details</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="username" style={styles.label}>
                        <FaUser /> Name
                      </label>
                      <input
                        id="username"
                        style={styles.input}
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="mobilenumber" style={styles.label}>
                        <FaPhoneAlt /> Mobile Number
                      </label>
                      <input
                        id="mobilenumber"
                        style={styles.input}
                        type="tel"
                        name="mobilenumber"
                        value={formData.mobilenumber}
                        onChange={handleInputChange}
                        pattern="[0-9]{10}"
                        minLength={10}
                        maxLength={10}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="email" style={styles.label}>
                        <FaEnvelope /> Email
                      </label>
                      <input
                        id="email"
                        style={styles.input}
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="address" style={styles.label}>
                        <FaMapMarkerAlt /> Address
                      </label>
                      <input
                        id="address"
                        style={styles.input}
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="city" style={styles.label}>
                        <FaMapMarkerAlt /> City
                      </label>
                      <select
                        id="city"
                        style={styles.select}
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((city) => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="area" style={styles.label}>
                        <FaMapMarkerAlt /> Area
                      </label>
                      <select
                        id="area"
                        style={styles.select}
                        name="area"
                        value={formData.area}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.city}
                      >
                        <option value="">Select Area</option>
                        {availableAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="language" style={styles.label}>
                        <FaLanguage /> Preferred Language
                      </label>
                      <input
                        id="language"
                        style={styles.input}
                        type="text"
                        name="language"
                        placeholder="Hindi, English, Kannada etc"
                        value={formData.language}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="gender" style={styles.label}>
                        <FaUserTag /> Gender
                      </label>
                      <select
                        id="gender"
                        style={styles.select}
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="age" style={styles.label}>
                        <FaBirthdayCake /> Age
                      </label>
                      <input
                        id="age"
                        style={styles.input}
                        type="text"
                        name="age"
                        placeholder="Enter age"
                        value={formData.age}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="urgencyStatus" style={styles.label}>
                        <FaClock /> Urgency
                      </label>
                      <select
                        id="urgencyStatus"
                        style={styles.select}
                        name="urgencyStatus"
                        value={formData.urgencyStatus}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="immediate">Immediate</option>
                        <option value="within_1_week">Within 1 Week</option>
                        <option value="just_enquiry">Just Enquiry</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="annualIncome" style={styles.label}>
                        <FaMoneyBillWave /> Annual Income
                      </label>
                      <input
                        id="annualIncome"
                        style={styles.input}
                        type="text"
                        name="annualIncome"
                        placeholder="e.g. 500000"
                        value={formData.annualIncome}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="sumAssured" style={styles.label}>
                        <FaShieldAlt /> Sum Assured
                      </label>
                      <input
                        id="sumAssured"
                        style={styles.input}
                        type="text"
                        name="sumAssured"
                        placeholder="e.g. 1000000"
                        value={formData.sumAssured}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="smokerStatus" style={styles.label}>
                        <FaInfoCircle /> Smoker Status
                      </label>
                      <select
                        id="smokerStatus"
                        style={styles.select}
                        name="smokerStatus"
                        value={formData.smokerStatus}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="smoker">Smoker</option>
                        <option value="non-smoker">Non-Smoker</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="insurancePlan" style={styles.label}>
                        <FaShieldAlt /> Insurance Plan
                      </label>
                      <select
                        id="insurancePlan"
                        style={styles.select}
                        name="insurancePlan"
                        value={formData.insurancePlan}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Insurance Plan</option>
                        {INSURANCE_OPTIONS.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    <FaInfoCircle />
                    Summary & Notes
                  </h3>
                  <p style={styles.helper}>
                    Review your selected insurance plan and share additional
                    requirements.
                  </p>

                  <div style={styles.summaryBox}>
                    {selectedInsuranceLabel && (
                      <p>
                        <strong>Selected Plan:</strong> {selectedInsuranceLabel}
                      </p>
                    )}

                    {formData.annualIncome && (
                      <p>
                        <strong>Annual Income:</strong> {formData.annualIncome}
                      </p>
                    )}

                    {formData.sumAssured && (
                      <p>
                        <strong>Sum Assured:</strong> {formData.sumAssured}
                      </p>
                    )}

                    {formData.smokerStatus && (
                      <p>
                        <strong>Smoker Status:</strong> {formData.smokerStatus}
                      </p>
                    )}
                  </div>

                  <div style={{ marginTop: "14px" }}>
                    <label htmlFor="additionalNotes" style={styles.label}>
                      <FaInfoCircle /> Notes
                    </label>
                    <textarea
                      id="additionalNotes"
                      style={styles.textarea}
                      name="additionalNotes"
                      placeholder="Mention existing policy details, family needs, health concerns, preferred premium range, etc."
                      value={formData.additionalNotes}
                      onChange={handleInputChange}
                    />
                  </div>

                  {submitError && <p style={styles.errorText}>{submitError}</p>}
                </div>

                <div style={styles.submitWrap}>
                  <button
                    type="submit"
                    style={styles.submitBtn}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Requirement"}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <form style={styles.formCard} onSubmit={handleSearchCheckLabour}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Search Insurance Agent</h2>
                <p style={styles.formSub}>
                  Select gender, city, and area to find available life insurance
                  agents near your location.
                </p>
              </div>

              <div style={styles.body}>
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Search Filters</h3>

                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="search-gender" style={styles.label}>
                        <FaUserTag /> Gender
                      </label>
                      <select
                        id="search-gender"
                        style={styles.select}
                        name="gender"
                        value={searchCriteria.gender}
                        onChange={handleSearchChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="search-city" style={styles.label}>
                        <FaMapMarkerAlt /> City
                      </label>
                      <select
                        id="search-city"
                        style={styles.select}
                        name="city"
                        value={searchCriteria.city}
                        onChange={handleSearchChange}
                        required
                      >
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((city) => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="search-area" style={styles.label}>
                        <FaMapMarkerAlt /> Area
                      </label>
                      <select
                        id="search-area"
                        style={styles.select}
                        name="area"
                        value={searchCriteria.area}
                        onChange={handleSearchChange}
                        required
                        disabled={!searchCriteria.city}
                      >
                        <option value="">Select Area</option>
                        {searchAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={styles.submitWrap}>
                    <button
                      type="submit"
                      style={styles.submitBtn}
                      disabled={isSearching}
                    >
                      {isSearching ? "Searching..." : "Search Agent"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default LifeInsuranceAgent;